import { Product } from '@shared/models/product';
import { DynamicPriceRulesEntityId } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-entity-id';

/**
 * Stateless utility that derives which external entities a set of products' dynamic
 * price rules depend on.
 *
 * Background: when an admin configures dynamic pricing for a product, each rule
 * references an entity (boat, berth, season, ...) via its `entityId` field.
 * `BcmProductDynamicPriceMap.getRequirements()` collects all referenced entity IDs
 * for a single product. This class aggregates them across multiple products and
 * provides a clean query API so callers do not have to iterate rules themselves.
 *
 * Usage:
 *   const req = new PriceRuleRequirements(products);
 *   if (req.requires(DynamicPriceRulesEntityId.Boat)) { // show boat selector }
 *
 * Extending to new entity types requires no changes here — the class is driven
 * entirely by the values returned by getRequirements(), which in turn reflects
 * whatever entities are configured in DynamicPriceRulesEntityId.
 */
export class PriceRuleRequirements {
    private readonly _requirements: Set<string>;

    /**
     * @param products The products attached to the currently selected tenant relations.
     *   Only products with hasDynamicPrice = true contribute requirements.
     */
    constructor(products: Product[]) {
        this._requirements = new Set(
            (products || [])
                .filter(p => p.hasDynamicPrice)
                .flatMap(p => p.priceRuleMap?.getRequirements() ?? [])
        );
    }

    /**
     * Returns true when at least one product's price rules reference the given entity.
     * Use this to decide whether to render the corresponding form widget in the UI.
     */
    requires(entity: DynamicPriceRulesEntityId): boolean {
        return this._requirements.has(entity);
    }

    /** True when any dynamic pricing requirements were found across the given products. */
    get hasAny(): boolean {
        return this._requirements.size > 0;
    }
}
